<template>
  <div class="fondo">
    <v-container fluid fill-height>
      <v-layout align-center justify-center>
        <v-flex xs12 sm8 md5 lg4>
          <v-card class="login-card elevation-24" shaped>
            <!-- Header con logo y título -->
            <div class="login-header">
              <div class="logo-container">
                <img src="@/assets/logo.svg" alt="SIGMAC GYM" class="login-logo" />
              </div>
              <h2 class="login-title">Bienvenido</h2>
              <p class="login-subtitle">Inicia sesión en tu cuenta</p>
            </div>

            <!-- Paso 1: Credenciales -->
            <v-card-text v-if="paso === 1" class="login-form-container">
              <v-form ref="form" v-model="formularioValido" lazy-validation>
                <div class="input-group">
                  <v-text-field
                    v-model="email"
                    prepend-inner-icon="email"
                    label="Correo electrónico"
                    type="email"
                    outlined
                    color="primary"
                    :rules="reglasEmail"
                    class="custom-input"
                    hide-details="auto"
                    @keyup.enter="verificarEmpresas"
                  ></v-text-field>
                </div>
                <div class="input-group">
                  <v-text-field
                    v-model="password"
                    prepend-inner-icon="lock"
                    :append-icon="mostrarPassword ? 'visibility' : 'visibility_off'"
                    @click:append="mostrarPassword = !mostrarPassword"
                    label="Contraseña"
                    :type="mostrarPassword ? 'text' : 'password'"
                    outlined
                    color="primary"
                    :rules="reglasPassword"
                    class="custom-input"
                    hide-details="auto"
                    @keyup.enter="verificarEmpresas"
                  ></v-text-field>
                </div>
              </v-form>
            </v-card-text>

            <!-- Paso 2: Selector de empresa (solo si unica=false) -->
            <v-card-text v-if="paso === 2" class="login-form-container">
              <p class="selector-label">Selecciona tu empresa:</p>
              <v-list dense>
                <v-list-item
                  v-for="empresa in empresas"
                  :key="empresa.dba"
                  :class="{ 'empresa-activa': dbaSeleccionada === empresa.dba }"
                  class="empresa-item"
                  @click="dbaSeleccionada = empresa.dba"
                >
                  <v-list-item-icon>
                    <v-icon color="primary">business</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ empresa.nombre }}</v-list-item-title>
                    <v-list-item-subtitle>{{ empresa.rol }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action v-if="dbaSeleccionada === empresa.dba">
                    <v-icon color="primary">check_circle</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>

            <!-- Botones de acción -->
            <v-card-actions class="login-actions">
              <v-btn
                v-if="paso === 1"
                color="primary"
                large
                :loading="cargando"
                :disabled="!formularioValido"
                @click="verificarEmpresas"
                class="login-btn"
                block
                shaped
              >
                <v-icon left>arrow_forward</v-icon>
                Continuar
              </v-btn>
              <div v-if="paso === 2" style="width:100%; display:flex; gap:8px;">
                <v-btn outlined color="primary" @click="paso = 1" :disabled="cargando">
                  <v-icon left>arrow_back</v-icon>Volver
                </v-btn>
                <v-btn
                  color="primary"
                  large
                  :loading="cargando"
                  :disabled="!dbaSeleccionada"
                  @click="iniciarSesion"
                  class="login-btn"
                  style="flex:1"
                  shaped
                >
                  <v-icon left>login</v-icon>
                  Ingresar
                </v-btn>
              </div>
            </v-card-actions>

            <!-- Divider y registro -->
            <div class="divider-section">
              <v-divider></v-divider>
              <span class="divider-text">o</span>
              <v-divider></v-divider>
            </div>

            <div class="register-section">
              <p class="register-text">
                ¿No tienes cuenta?
                <v-btn
                  text
                  color="primary"
                  small
                  class="register-link"
                >
                  Regístrate aquí
                </v-btn>
              </p>
            </div>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Snackbar mejorado -->
      <v-snackbar
        v-model="mostrarMensaje"
        :timeout="4000"
        :color="mensaje.color"
        top
        shaped
        class="custom-snackbar"
      >
        <div class="snackbar-content">
          <v-icon class="snackbar-icon">
            {{ mensaje.color === 'success' ? 'check_circle' : 'error' }}
          </v-icon>
          {{ mensaje.texto }}
        </div>
        <v-btn
          icon
          @click="mostrarMensaje = false"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </v-snackbar>

    </v-container>
  </div>
</template>

<script>
// TAREA-02: Login migrado a API-SIGMA-CLOUD (2026-05-11).
// Antes usaba HttpService → usuarios.php (PHP muerto).
// Ahora usa ApiService.login() → POST /api/auth/login en el gateway multi-tenant.
// La respuesta incluye el JWT y el db_tenant, que App.vue guarda en localStorage
// para que ApiService los use automáticamente en cada request posterior.
import ApiService from '../../Servicios/ApiService'

export default {
  name: "Login",

  data: () => ({
    paso: 1,
    email: "",
    password: "",
    empresas: [],
    dbaSeleccionada: null,
    mostrarPassword: false,
    formularioValido: true,
    cargando: false,
    mensaje: { texto: "", color: "" },
    mostrarMensaje: false,
    reglasEmail: [
      v => !!v || 'El correo es requerido',
      v => /.+@.+\..+/.test(v) || 'Ingresa un correo válido',
    ],
    reglasPassword: [
      v => !!v || 'La contraseña es requerida',
      v => (v && v.length >= 6) || 'La contraseña debe tener al menos 6 caracteres',
    ],
  }),

  methods: {
    // Paso 1: verifica credenciales y obtiene la lista de empresas.
    async verificarEmpresas () {
      if (!this.$refs.form.validate()) return
      this.cargando = true
      try {
        const res = await ApiService.getEmpresas({ email: this.email, paswd: this.password })

        if (!res.success || !res.empresas || res.empresas.length === 0) {
          this.mostrarMensajeError('No tienes empresas asignadas')
          return
        }

        if (res.unica) {
          // Una sola empresa: saltar el selector e ir directo al login
          this.dbaSeleccionada = res.empresas[0].dba
          await this.iniciarSesion()
        } else {
          // Varias empresas: mostrar selector
          this.empresas = res.empresas
          this.paso = 2
        }
      } catch (error) {
        console.error('[empresas] error:', error.response || error.message || error)
        const status = error.response && error.response.status
        const serverMsg = error.response && error.response.data && error.response.data.message
        const msg = status === 401
          ? 'Credenciales incorrectas'
          : serverMsg || ('Error ' + (status || error.message || 'de red'))
        this.mostrarMensajeError(msg)
      } finally {
        this.cargando = false
      }
    },

    // Paso 2: hace el login real con la empresa seleccionada.
    async iniciarSesion () {
      this.cargando = true
      try {
        const resultado = await ApiService.login({
          email: this.email,
          paswd: this.password,
          dba:   this.dbaSeleccionada
        })
        this.$emit('logeado', resultado)
      } catch (error) {
        console.error('[login] error:', error.response || error.message || error)
        const status = error.response && error.response.status
        const serverMsg = error.response && error.response.data && error.response.data.message
        const msg = status === 401
          ? 'Credenciales incorrectas'
          : serverMsg || ('Error ' + (status || error.message || 'de red'))
        this.mostrarMensajeError(msg)
        this.paso = 1
      } finally {
        this.cargando = false
      }
    },

    mostrarMensajeExito (texto) {
      this.mostrarMensaje = true
      this.mensaje.texto = texto
      this.mensaje.color = "success"
    },

    mostrarMensajeError (texto) {
      this.mostrarMensaje = true
      this.mensaje.texto = texto
      this.mensaje.color = "error"
    }
  },
}
</script>

<style scoped>
/* ── Fondo ──────────────────────────────────────────────────────────────── */
.fondo {
  background: #051424;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
}

.fondo::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 20% 40%, rgba(19, 236, 19, 0.07) 0%, transparent 70%),
    radial-gradient(ellipse 40% 40% at 80% 70%, rgba(19, 236, 19, 0.04) 0%, transparent 70%);
  pointer-events: none;
}

/* ── Card ───────────────────────────────────────────────────────────────── */
.login-card {
  background: #0d1c2d !important;
  border: 1px solid rgba(19, 236, 19, 0.15) !important;
  border-radius: 16px !important;
  max-width: 440px;
  margin: 0 auto;
  overflow: hidden;
  animation: fadeInUp 0.5s ease-out;
  box-shadow: 0 0 40px rgba(19, 236, 19, 0.08) !important;
}

/* ── Header ─────────────────────────────────────────────────────────────── */
.login-header {
  background: linear-gradient(160deg, #0d1f13 0%, #0a1a0a 100%);
  border-bottom: 1px solid rgba(19, 236, 19, 0.1);
  text-align: center;
  padding: 40px 30px 30px;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.login-logo {
  width: 90px;
  height: 90px;
  filter: drop-shadow(0 0 12px rgba(19, 236, 19, 0.4));
}

.login-title {
  font-size: 26px;
  font-weight: 700;
  color: #d4e4fa;
  margin-bottom: 6px;
  letter-spacing: -0.3px;
}

.login-subtitle {
  font-size: 14px;
  color: #6b8a9a;
  margin: 0;
}

/* ── Formulario ─────────────────────────────────────────────────────────── */
.login-form-container {
  padding: 32px 28px 16px;
}

.input-group {
  margin-bottom: 20px;
}

.custom-input >>> .v-input__slot {
  border-radius: 10px !important;
  background: #122131 !important;
}

.custom-input >>> fieldset {
  border-color: rgba(19, 236, 19, 0.2) !important;
}

.custom-input:hover >>> fieldset {
  border-color: rgba(19, 236, 19, 0.4) !important;
}

/* ── Acciones ───────────────────────────────────────────────────────────── */
.login-actions {
  padding: 0 28px 28px;
}

.login-btn {
  height: 48px !important;
  font-size: 15px !important;
  font-weight: 700;
  text-transform: none !important;
  letter-spacing: 0.3px;
  border-radius: 10px !important;
  box-shadow: 0 0 20px rgba(19, 236, 19, 0.25) !important;
  transition: all 0.2s ease !important;
}

.login-btn:hover {
  box-shadow: 0 0 30px rgba(19, 236, 19, 0.4) !important;
  transform: translateY(-1px);
}

/* ── Selector de empresa ────────────────────────────────────────────────── */
.selector-label {
  color: #6b8a9a;
  font-size: 13px;
  margin-bottom: 10px;
}

.empresa-item {
  border-radius: 8px;
  margin-bottom: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s;
}

.empresa-activa {
  background: rgba(19, 236, 19, 0.08) !important;
  border-color: rgba(19, 236, 19, 0.3) !important;
}

/* ── Divider / Registro ─────────────────────────────────────────────────── */
.divider-section {
  display: flex;
  align-items: center;
  padding: 0 28px;
  margin: 4px 0;
}

.divider-text {
  margin: 0 12px;
  color: #2d4a5a;
  font-size: 13px;
}

.register-section {
  text-align: center;
  padding: 12px 28px 28px;
}

.register-text {
  color: #4a6a7a;
  font-size: 13px;
  margin: 0;
}

/* ── Snackbar ───────────────────────────────────────────────────────────── */
.custom-snackbar { z-index: 9999; }

.snackbar-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.snackbar-icon { font-size: 18px; }

/* ── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 600px) {
  .login-card { margin: 16px; max-width: none; }
  .login-header { padding: 30px 20px 24px; }
  .login-title { font-size: 22px; }
  .login-form-container { padding: 24px 20px 12px; }
  .login-actions { padding: 0 20px 24px; }
}

/* ── Animación ──────────────────────────────────────────────────────────── */
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>


// WEBPACK FOOTER //
// src/components/Usuarios/Login.vue